import { usageApi } from "@/features/usage/api";

/**
 * USAGE-ANALYTICS-1 / C-322 — the usage beacon caller.
 *
 * ⚑ IT SENDS NOTHING UNTIL THE SERVER SAYS IT MAY. C-323 §4: "client sends usage events ONLY when
 * GET /api/usage/preference says allowed". That read is the DESIGNATED one — the server derives
 * `enabled` and `consent_required` itself so the client never works out a region — which is why
 * consulting it is not the client deciding. The events route, by contrast, answers 202 whether or
 * not a row was written, on purpose, so NOTHING here looks at that response.
 *
 * The distinction took me two wrong recommendations to see, and it is the whole design: reading a
 * server's published answer is fine; inferring the answer, or reading it off the beacon's reply,
 * is the client deciding what gets recorded.
 *
 * ⚑ `allowed` IS TRI-STATE, and the third state is the point. Before the preference read resolves
 * the answer is UNKNOWN, and unknown means silence. A beacon sent on an assumption is exactly the
 * defect §4 exists to prevent, and "probably allowed" is an assumption.
 *
 * ⚑ THE BODY CARRIES FOUR KEYS AND NO MORE. `UsageEventIn` is `extra="forbid"`, so an extra key is
 * a 422 rather than a stored surprise — and identity, the clock and the device class are ABSENT BY
 * DESIGN because the server takes them from the authenticated principal and the request's own
 * user-agent: "a body cannot even attempt to assert them." Sending `user_id` here would not be
 * helpful, it would be rejected.
 *
 * ⚑ `route` IS A DECLARED TEMPLATE OR IT IS OMITTED. The server resolves it to a template or a
 * sentinel and NEVER trusts the string sent, so posting a raw `/teachers/abc123` would achieve
 * nothing except putting an id on the wire. FA-2's drift fence runs both ways, so a new page route
 * is declared on the server list in the same PR as the page — not papered over here.
 */

/** The closed vocabulary, mirrored from `usage_events.USAGE_EVENT_TYPES`. A guard reads the
 *  Python and fails if the two ever diverge, per CLAUDE.md's preference for a guard over a note. */
export const USAGE_EVENTS = Object.freeze({
  PAGE_VIEW: "page_view",
  RECORD_START: "record_start",
  UPLOAD: "upload",
  SHARE: "share",
  OPEN_FEEDBACK: "open_feedback",
  SET_GOAL: "set_goal",
  SCHEDULE: "schedule",
  MIRA_OPENED: "mira_opened",
  MIRA_MESSAGE_SENT: "mira_message_sent",
  MIRA_SESSION_END: "mira_session_end",
});

const EVENT_VALUES = Object.freeze(Object.values(USAGE_EVENTS));

/** `visible_ms` is for this event and no other — the service says so in as many words. */
export const VISIBLE_MS_EVENT = USAGE_EVENTS.PAGE_VIEW;

const SESSION_KEY = "cognivio.usage.session";

/** unknown | true | false. Starts unknown, which means silence. */
let allowed = null;

/**
 * A per-tab id, so events from one sitting can be grouped without identifying anyone.
 *
 * sessionStorage can be BLOCKED — private mode, disabled cookies — and `RouteErrorBoundary` already
 * carries that lesson. A blocked store must not take the beacon down with it, so the id falls back
 * to an in-memory one for the life of the module.
 */
let memorySessionId = null;

const randomId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {
    /* fall through to the arithmetic id */
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export function usageSessionId() {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const made = randomId();
    window.sessionStorage.setItem(SESSION_KEY, made);
    return made;
  } catch (e) {
    if (!memorySessionId) memorySessionId = randomId();
    return memorySessionId;
  }
}

/** What the server last told us. `null` resets to unknown, which is silence. */
export function setUsageAllowed(next) {
  allowed = next === null || next === undefined ? null : Boolean(next);
}

export function usageAllowed() {
  return allowed;
}

/**
 * Ask the server whether recording is allowed, and remember the answer.
 *
 * On any failure the answer stays UNKNOWN rather than defaulting to allowed: a network blip must
 * not become consent. Returns the payload for a caller that also wants `consent_required` to
 * render the Settings control.
 */
export async function refreshUsageAllowed() {
  try {
    const res = await usageApi.preference();
    const data = (res && res.data) || {};
    setUsageAllowed(data.enabled);
    return data;
  } catch (e) {
    setUsageAllowed(null);
    return null;
  }
}

/**
 * Send one beacon, if the server has said we may.
 *
 * Returns true only when a request was actually made — for tests and for nothing else. It never
 * reports whether a row was written, because the route does not say and must not.
 */
export async function sendUsageEvent(event, { route, visibleMs } = {}) {
  if (allowed !== true) return false;
  if (!EVENT_VALUES.includes(event)) return false;

  const body = { event, session_id: usageSessionId() };
  if (route) body.route = route;
  // Only page_view carries it, and only as a whole number of milliseconds.
  if (event === VISIBLE_MS_EVENT && Number.isFinite(visibleMs) && visibleMs >= 0) {
    body.visible_ms = Math.round(visibleMs);
  }

  try {
    await usageApi.postEvent(body);
  } catch (e) {
    // A failed beacon is not the teacher's problem and not a surface. It is dropped in silence:
    // no retry, because a retry on an unknown outcome is how one action becomes two rows.
  }
  return true;
}

export default sendUsageEvent;
